import { useState } from "react";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { api, formatApiError, formatCurrency } from "@/lib/api";
import { toast } from "@/components/ui/sonner";

const emptyForm = { name: "", category: "fashion", description: "", price: "", stock: "", image_url: "", badge: "", featured: false };

export function ProductManager({ products, onRefresh }) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setOpen(true);
  };

  const openEdit = (product) => {
    setEditing(product);
    setForm({ ...product, price: String(product.price), stock: String(product.stock), badge: product.badge || "" });
    setOpen(true);
  };

  const submit = async (event) => {
    event.preventDefault();
    setSaving(true);
    const payload = { ...form, price: Number(form.price), stock: Number(form.stock), badge: form.badge || null };
    try {
      if (editing) await api.put(`/admin/products/${editing.id}`, payload);
      else await api.post("/admin/products", payload);
      toast.success(editing ? "Product updated" : "Product created");
      setOpen(false);
      await onRefresh();
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (product) => {
    if (!window.confirm(`Delete ${product.name}?`)) return;
    try {
      await api.delete(`/admin/products/${product.id}`);
      toast.success("Product deleted");
      await onRefresh();
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  return (
    <section data-testid="admin-product-manager">
      <div className="mb-6 flex items-center justify-between">
        <h2 className="font-heading text-4xl font-black italic">Products</h2>
        <Button data-testid="add-product-button" onClick={openCreate} className="kinetic-button rounded-none bg-volt text-black hover:bg-transparent hover:text-volt"><Plus size={16} /> Add product</Button>
      </div>
      <div className="border border-white/10 bg-white/[0.02]">
        <Table>
          <TableHeader>
            <TableRow className="admin-table-row"><TableHead>Product</TableHead><TableHead>Category</TableHead><TableHead>Price</TableHead><TableHead>Stock</TableHead><TableHead className="text-right">Actions</TableHead></TableRow>
          </TableHeader>
          <TableBody>
            {products.map((product) => (
              <TableRow key={product.id} className="admin-table-row" data-testid={`admin-product-row-${product.id}`}>
                <TableCell><div className="flex items-center gap-3"><img src={product.image_url} alt={product.name} className="h-14 w-12 object-cover" /><span className="font-bold">{product.name}</span></div></TableCell>
                <TableCell className="capitalize">{product.category}</TableCell>
                <TableCell>{formatCurrency(product.price)}</TableCell>
                <TableCell data-testid={`admin-stock-${product.id}`}>{product.stock}</TableCell>
                <TableCell className="text-right">
                  <button data-testid={`edit-product-${product.id}`} onClick={() => openEdit(product)} className="mr-3 text-zinc-400 hover:text-volt"><Pencil size={17} /></button>
                  <button data-testid={`delete-product-${product.id}`} onClick={() => remove(product)} className="text-zinc-400 hover:text-neon"><Trash2 size={17} /></button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl border-white/10 bg-void text-foreground" data-testid="product-form-dialog">
          <DialogHeader><DialogTitle className="font-heading text-4xl font-black italic">{editing ? "Edit product" : "New product"}</DialogTitle><DialogDescription className="text-zinc-500">Update the product story, pricing, stock, and editorial imagery.</DialogDescription></DialogHeader>
          <form onSubmit={submit} className="grid gap-4 sm:grid-cols-2">
            <Input data-testid="product-name-input" required placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-none bg-white/[0.03]" />
            <Select value={form.category} onValueChange={(value) => setForm({ ...form, category: value })}>
              <SelectTrigger data-testid="product-category-select" className="rounded-none bg-white/[0.03]"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-zinc-950 text-foreground"><SelectItem value="fashion">Fashion</SelectItem><SelectItem value="electronics">Electronics</SelectItem><SelectItem value="lifestyle">Lifestyle</SelectItem></SelectContent>
            </Select>
            <Input data-testid="product-price-input" required type="number" min="1" placeholder="Price INR" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="rounded-none bg-white/[0.03]" />
            <Input data-testid="product-stock-input" required type="number" min="0" placeholder="Stock" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} className="rounded-none bg-white/[0.03]" />
            <Input data-testid="product-badge-input" placeholder="Badge" value={form.badge} onChange={(e) => setForm({ ...form, badge: e.target.value })} className="rounded-none bg-white/[0.03]" />
            <label className="flex items-center gap-3 text-sm text-zinc-400"><input data-testid="product-featured-checkbox" type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} /> Featured</label>
            <Input data-testid="product-image-input" required placeholder="Image URL" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} className="rounded-none bg-white/[0.03] sm:col-span-2" />
            <Textarea data-testid="product-description-input" required placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="min-h-28 rounded-none bg-white/[0.03] sm:col-span-2" />
            <Button data-testid="save-product-button" disabled={saving} className="kinetic-button h-12 rounded-none bg-volt font-bold uppercase tracking-[0.18em] text-black hover:bg-transparent hover:text-volt sm:col-span-2">{saving ? "Saving..." : "Save product"}</Button>
          </form>
        </DialogContent>
      </Dialog>
    </section>
  );
}
